import { TimeEntry } from '../models/types';
import { format, startOfWeek, endOfWeek, eachDayOfInterval } from 'date-fns';
import { formatDuration, calculateNetWorkDuration } from './timeUtils';
import jsPDF from 'jspdf';
import { trackEvent } from './analytics';
import { db } from '../services/db';
import { APP_VERSION, versionForFilename } from './appVersion';

export interface DatabaseBackupPayload {
  appName: string;
  appVersion: string;
  exportedAt: string;
  data: {
    timeEntries: TimeEntry[];
    projects: unknown[];
    userInfo: unknown[];
    pendingVoiceCommands: unknown[];
    timesheetSubmissions: unknown[];
    shoppingItems: unknown[];
    todoItems: unknown[];
  };
}

export interface DatabaseBackupSourceData {
  timeEntries: TimeEntry[];
  projects: unknown[];
  userInfo: unknown[];
  pendingVoiceCommands: unknown[];
  timesheetSubmissions: unknown[];
  shoppingItems: unknown[];
  todoItems: unknown[];
}

export const createDatabaseBackupPayload = (data: DatabaseBackupSourceData): DatabaseBackupPayload => {
  return {
    appName: 'Time Trakr Lite',
    appVersion: APP_VERSION,
    exportedAt: new Date().toISOString(),
    data
  };
};

export const buildDatabaseBackupPayload = async (): Promise<DatabaseBackupPayload> => {
  const [
    timeEntries,
    projects,
    userInfo,
    pendingVoiceCommands,
    timesheetSubmissions,
    shoppingItems,
    todoItems
  ] = await Promise.all([
    db.getAllTimeEntries(),
    db.projects.toArray(),
    db.userInfo.toArray(),
    db.pendingVoiceCommands.toArray(),
    db.timesheetSubmissions.toArray(),
    db.shoppingItems.toArray(),
    db.todoItems.toArray()
  ]);

  return createDatabaseBackupPayload({
    timeEntries,
    projects,
    userInfo,
    pendingVoiceCommands,
    timesheetSubmissions,
    shoppingItems,
    todoItems
  });
};

export const exportDatabaseBackup = async (): Promise<void> => {
  const backup = await buildDatabaseBackupPayload();
  const blob = new Blob([JSON.stringify(backup, null, 2)], {
    type: 'application/json;charset=utf-8'
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  const now = new Date();
  const timestamp = format(now, 'yyyyMMdd-HHmmss');
  link.setAttribute('href', url);
  // Version first, then timestamp: a folder holding several months of
  // backups then sorts into a sensible order instead of interleaving builds.
  link.setAttribute('download', `TimeTrakrLite-backup-${versionForFilename()}-${timestamp}.json`);
  link.style.visibility = 'hidden';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.setTimeout(() => URL.revokeObjectURL(url), 60000);

  trackEvent('export', { format: 'backup-json', version: APP_VERSION });
};

export const exportToCsv = (entries: TimeEntry[]): void => {
  // Sort entries by date and time
  const sortedEntries = [...entries].sort((a, b) => 
    new Date(a.timeIn).getTime() - new Date(b.timeIn).getTime()
  );

  // Create TSV header
  const headers = [
    'ID', 'Name', 'Date', 'Time In', 'Time Out', 'Project', 'Task', 'Location', 
    'Notes', 'Duration (minutes)', 'Duration (hh:mm)', 'Verified', 'Last Modified'
  ];
  
  let tsvContent = headers.join('\t') + '\n';

  // Add each time entry to the TSV
  sortedEntries.forEach(entry => {
    const row = [
      entry.entryId || entry.id || '',
      entry.name || 'Anonymous',
      format(entry.timeIn, 'M/d/yyyy'),
      format(entry.timeIn, 'H:mm'),
      entry.timeOut ? format(entry.timeOut, 'H:mm') : '',
      entry.project || '',
      entry.task || '',
      entry.location || '',
      entry.notes || '', // No need to escape for TSV
      entry.timeOut ? calculateNetWorkDuration(entry).toString() : '',
      entry.timeOut ? formatDuration(calculateNetWorkDuration(entry)) : '',
      entry.verified ? 'Yes' : 'No',
      entry.lastModified ? format(entry.lastModified, 'M/d/yyyy H:mm:ss') : ''
    ];

    // Join with tabs (TSV format)
    tsvContent += row.join('\t') + '\n';
  });

  // Create a blob and trigger download
  // Use .txt for tab-separated values for Excel
  const blob = new Blob([tsvContent], { type: 'text/plain;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  // Filename: YYYY-DD-MM_<username>_TimeTrakrLite_<timestamp>.txt
  const now = new Date();
  const datePart = format(now, 'yyyy-dd-MM');
  const timestamp = format(now, 'HHmmss');
  const userName = (entries[0]?.name || 'Anonymous')
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-');
  link.setAttribute('href', url);
  link.setAttribute('download', `${datePart}_${userName}_TimeTrakrLite_${timestamp}.txt`);
  link.style.visibility = 'hidden';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  // Track CSV export in analytics
  trackEvent('export', { format: 'csv' });
};

export const exportAllToCsv = async (): Promise<void> => {
  // getAllTimeEntries is deliberately unfiltered so backups stay complete.
  // An export the employee hands to the office is not a backup: a shift they
  // deleted must not appear on it.
  const entries = (await db.getAllTimeEntries()).filter(entry => !entry.deletedAt);
  exportToCsv(entries);
};

export const exportTimesheetToTsv = (entries: TimeEntry[], userName: string, weekStartDate: Date): string => {
  const weekStart = startOfWeek(weekStartDate);
  const weekEnd = endOfWeek(weekStartDate);
  
  // Create TSV header for timesheet
  let tsvContent = 'Employee Name\tWeek Period\tSubmission Date\n';
  tsvContent += `${userName}\t${format(weekStart, 'MMM d, yyyy')} - ${format(weekEnd, 'MMM d, yyyy')}\t${format(new Date(), 'MMM d, yyyy')}\n\n`;
  
  // Define columns for timesheet (without entry ID and last modified)
  const headers = [
    'Date', 'Time In', 'Time Out', 'Project', 'Task', 'Location', 
    'Hours', 'Notes', 'Verified'
  ];
  tsvContent += headers.join('\t') + '\n';

  // Group entries by date
  const entriesByDate = entries.reduce<Record<string, TimeEntry[]>>((acc, entry) => {
    const dateKey = format(entry.timeIn, 'yyyy-MM-dd');
    if (!acc[dateKey]) {
      acc[dateKey] = [];
    }
    acc[dateKey].push(entry);
    return acc;
  }, {});

  // Add each time entry to the CSV, grouped by date
  Object.keys(entriesByDate).sort().forEach(dateKey => {
    const dayEntries = entriesByDate[dateKey];
    let isFirstEntryOfDay = true;
    
    dayEntries.forEach(entry => {
      const row = [
        isFirstEntryOfDay ? format(entry.timeIn, 'EEE, MMM d, yyyy') : '',
        format(entry.timeIn, 'HH:mm'),
        entry.timeOut ? format(entry.timeOut, 'HH:mm') : '--:--',
        entry.project || '',
        entry.task || '',
        entry.location || '',
        entry.timeOut ? (calculateNetWorkDuration(entry) / 60).toFixed(2) : '--',
        entry.notes ? `"${entry.notes.replace(/"/g, '""')}"` : '',
        entry.verified ? 'Yes' : 'No'
      ];

      // Join with tabs (TSV format)
      tsvContent += row.join('\t') + '\n';
      
      isFirstEntryOfDay = false;
    });
    
    // Add a blank line after each day
    tsvContent += '\n';
  });

  // Add signature placeholder
  tsvContent += '\nEmployee Signature:\t_______________________\tDate:\t_______________________\n';
  
  return tsvContent;
};

// Helper function to format time in 12-hour format with am/pm
const formatTime12Hour = (date: Date): string => {
  return format(date, 'h:mm a').toLowerCase();
};

// Helper function to format duration in hours with 2 decimal places
const formatHours = (minutes: number): string => {
  return (minutes / 60).toFixed(2);
};

export const generateTimesheetPdf = (
  entries: TimeEntry[], 
  userName: string, 
  weekStartDate: Date, 
  signature: string = '', 
  signatureDate: string = format(new Date(), 'yyyy-MM-dd')
): void => {
  try {
    const weekEnd = endOfWeek(weekStartDate);
    const weekRange = `${format(weekStartDate, 'MMM d')} – ${format(weekEnd, 'MMM d, yyyy')}`;
    const currentDateTime = format(new Date(), 'MMM d, yyyy h:mm a');
    
    // Initialize jsPDF with US Letter size
    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'letter'
    });
    
    // Track row index for alternating colors (currently unused but kept for future implementation)
    // let rowIndex = 0;

    // Set default font
    doc.setFont('helvetica');
    
    // Add company logo (if available)
    try {
      // This is a placeholder - the actual image should be in the public directory
      // Users can add their own CompanyLogo.png file to the public directory
      const logoUrl = '/CompanyLogo.png';
      doc.addImage(logoUrl, 'PNG', 15, 10, 20, 20);
    } catch (e) {
      console.log('Company logo not found, proceeding without it');
    }

    // Add title and employee info
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('Time Trakr Lite', doc.internal.pageSize.getWidth() / 2, 20, { align: 'center' });
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Employee Name: ${userName || 'Not Specified'}`, 15, 35);
    doc.text(`Week: ${weekRange}`, doc.internal.pageSize.getWidth() - 15, 35, { align: 'right' });

    // Group entries by day
    const daysOfWeek = eachDayOfInterval({ start: weekStartDate, end: weekEnd });
    const entriesByDay = daysOfWeek.map((day: Date) => {
      const dayStart = new Date(day);
      dayStart.setHours(0, 0, 0, 0);
      
      const dayEnd = new Date(day);
      dayEnd.setHours(23, 59, 59, 999);
      
      const dayEntries = entries
        .filter(entry => entry.timeIn >= dayStart && entry.timeIn <= dayEnd)
        .sort((a, b) => a.timeIn.getTime() - b.timeIn.getTime());
      
      const dayTotal = dayEntries.reduce((total, entry) => {
        return total + calculateNetWorkDuration(entry);
      }, 0);
      
      return {
        date: day,
        entries: dayEntries,
        totalMinutes: dayTotal
      };
    });

    // Filter out weekends with no entries
    const filteredDays = entriesByDay.filter((day: { entries: TimeEntry[]; date: Date }) => 
      day.entries.length > 0 || 
      (day.date.getDay() !== 0 && day.date.getDay() !== 6)
    );

    // Prepare table data
    // Custom PDF table layout: for each day, print a day summary row, then a header row, then entries
    let currentY = 50;
    const pageWidth = doc.internal.pageSize.getWidth();

    filteredDays.forEach((dayData: { entries: TimeEntry[]; date: Date; totalMinutes: number }) => {
      const entries = dayData.entries;
      if (entries.length === 0) return;
      // Day info row
      // Split the day label into parts to apply different formatting
      const dayName = format(dayData.date, 'EEEE');
      const startTime = entries.length ? formatTime12Hour(entries.reduce((min, e) => e.timeIn < min ? e.timeIn : min, entries[0].timeIn)) : '--:-- --';
      const endTime = entries.length && entries.some(e => e.timeOut) ? formatTime12Hour(entries.reduce((max, e) => (e.timeOut && e.timeOut > max ? e.timeOut : max), entries[0].timeIn)) : '--:-- --';
      const totalHours = (dayData.totalMinutes/60).toFixed(2);
      
      // Apply background for the entire row
      doc.setFillColor(220, 220, 220);
      doc.rect(15, currentY - 5, pageWidth - 30, 10, 'F');
      
      // Draw the day name with extra bold formatting
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(13);
      doc.text(dayName, 20, currentY);
      
      // Calculate position after day name
      const dayNameWidth = doc.getTextWidth(dayName);
      const startPosition = 25 + dayNameWidth;
      
      // Draw the start and end times with regular bold
      doc.setFontSize(12);
      doc.text(`Start ${startTime} - End ${endTime}`, startPosition, currentY);
      
      // Draw the total hours with extra emphasis
      doc.setFontSize(13);
      doc.text(`Total: ${totalHours} hrs`, pageWidth - 30, currentY, { align: 'right' });
      currentY += 8;

      // Table header row
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.text('Project', 20, currentY);
      doc.text('Task', 60, currentY);
      doc.text('Start', 110, currentY);
      doc.text('End', 130, currentY);
      doc.text('Total', 150, currentY);
      doc.text('Notes', 170, currentY);
      currentY += 6;

      // Draw table header line
      doc.setDrawColor(100, 100, 100);
      doc.setLineWidth(0.3);
      doc.line(15, currentY + 2, pageWidth - 15, currentY + 2);
      
      // Entry rows
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      entries.forEach((entry, index) => {
        // Add alternate row shading
        if (index % 2 === 1) {
          doc.setFillColor(245, 245, 245); // Light gray for alternate rows
          doc.rect(15, currentY - 4, pageWidth - 30, 6, 'F');
        }
        
        doc.text(entry.project || '', 20, currentY);
        doc.text(entry.task || '', 60, currentY);
        doc.text(formatTime12Hour(entry.timeIn), 110, currentY);
        doc.text(entry.timeOut ? formatTime12Hour(entry.timeOut) : '--:-- --', 130, currentY);
        doc.text(formatHours(calculateNetWorkDuration(entry)), 150, currentY, { align: 'left' });
        doc.text(entry.notes || '', 170, currentY);
        
        // Add horizontal line after each row
        doc.setDrawColor(200, 200, 200); // Light gray for row dividers
        doc.setLineWidth(0.1);
        doc.line(15, currentY + 3, pageWidth - 15, currentY + 3);
        
        currentY += 6;
        if (currentY > doc.internal.pageSize.getHeight() - 20) {
          doc.addPage();
          currentY = 20;
        }
      });
      currentY += 4;
    });

    // Week total row with enhanced styling
    doc.setFillColor(230, 230, 230);
    doc.rect(pageWidth - 150, currentY - 5, 135, 10, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.text(`Week Total: ${(entries.reduce((total, entry) => total + calculateNetWorkDuration(entry), 0)/60).toFixed(2)} hours`, pageWidth - 15, currentY, { align: 'right' });
    currentY += 10;

    // Add signature and timestamp
    const pageHeight = doc.internal.pageSize.getHeight();
    const signatureY = pageHeight - 40;
    
    // Add signature section
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.5);
    
    // Add signature line and date
    doc.line(15, signatureY + 15, 80, signatureY + 15);
    doc.setFontSize(10);
    doc.text('Employee Signature', 15, signatureY + 10);
    doc.text(`Date: ${signatureDate}`, 15, signatureY + 25);
    
    // Add signature image if available
    if (signature && signature.startsWith('data:image/')) {
      try {
        // Add the signature image
        doc.addImage(signature, 'PNG', 15, signatureY - 5, 80, 20);
      } catch (e) {
        console.error('Error adding signature to PDF:', e);
      }
    }
    
    // Add footer
    doc.setFontSize(9);
    doc.setTextColor(100);
    doc.text(`PDF created on ${currentDateTime}`, 15, pageHeight - 10);
    doc.text('TimeTrakrLite', doc.internal.pageSize.getWidth() - 15, pageHeight - 10, { align: 'right' });

    // Track PDF export in analytics
    trackEvent('export', { format: 'pdf' });
    
    // Save the PDF
    const timestamp = format(new Date(), 'yyyy-MM-dd-HHmmss');
    const safeUserName = (userName || 'Anonymous').replace(/[^\w\s-]/g, '').replace(/\s+/g, '-');
    doc.save(`${timestamp}-${safeUserName}-TimeTrakrLite-WeeklyTimesheet.pdf`);
    
  } catch (error) {
    console.error('Error generating PDF:', error);
    throw new Error('Failed to generate PDF. Please try again.');
  }
};
